import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getAllAttempts, getAttempts } from "../lib/rounding";
import type { Attempt, AttemptEvent } from "../lib/rounding";

const Admin: React.FC = () => {
  const { user, logout } = useAuth();
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null); // YYYY-MM-DD
  const [selectedAttempt, setSelectedAttempt] = useState<Attempt | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<number[]>([]);
  const [filterUser, setFilterUser] = useState<string>("");
  const [filterText, setFilterText] = useState<string>("");
  const [filterCorrect, setFilterCorrect] = useState<"all" | "correct" | "incorrect">("all");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filterStart, setFilterStart] = useState<string | null>(null);
  const [filterEnd, setFilterEnd] = useState<string | null>(null);
  const [serverItems, setServerItems] = useState<Attempt[]>([]);
  const [serverTotal, setServerTotal] = useState<number>(0);

  useEffect(() => {
    (async () => {
      setAttempts(await getAllAttempts());
    })();
  }, []);

  function toLocalDateStr(ms: number) {
    const d = new Date(ms);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const users = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of attempts) {
      m.set(a.user, (m.get(a.user) ?? 0) + 1);
    }
    return Array.from(m.keys()).sort();
  }, [attempts]);

  const attemptsByUserToday = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const out = new Map<string, number>();
    for (const a of attempts) {
      if (a.timestamp >= start) out.set(a.user, (out.get(a.user) ?? 0) + 1);
    }
    return out;
  }, [attempts]);

  function getPerDayCounts(user: string, days = 30) {
    const counts: Array<{ day: string; count: number }> = [];
    const today = new Date();
    for (let i = 0; i < days; i++) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
      const start = d.getTime();
      const end = start + 24 * 60 * 60 * 1000;
      const cnt = attempts.filter((a) => a.user === user && a.timestamp >= start && a.timestamp < end).length;
      counts.push({ day: toLocalDateStr(start), count: cnt });
    }
    return counts;
  }

  function getPerHourCounts(user: string, dayStr: string) {
    const parts = dayStr.split("-");
    const y = parseInt(parts[0] ?? "0", 10);
    const m = parseInt(parts[1] ?? "1", 10);
    const d = parseInt(parts[2] ?? "1", 10);
    const start = new Date(y, m - 1, d).getTime();
    const hours: Array<{ hour: number; count: number }> = [];
    for (let h = 0; h < 24; h++) {
      const s = start + h * 60 * 60 * 1000;
      const e = s + 60 * 60 * 1000;
      const cnt = attempts.filter((a) => a.user === user && a.timestamp >= s && a.timestamp < e).length;
      hours.push({ hour: h, count: cnt });
    }
    return hours;
  }

  // Group contiguous events of the same type (within GAP_MS) into summary groups
  function normalizeEventType(type: string) {
    const t = (type || "").toLowerCase();
    if (t === "keydown" || t === "key" || t === "input" || t === "input-change") return "typing";
    if (t === "paste") return "paste";
    if (t === "idle-start" || t === "idle-end") return "idle";
    if (t === "blur" || t === "visibilitychange" || t === "focus-lost") return "focus";
    if (t === "submit") return "submit";
    return t || "other";
  }

  function groupAttemptEvents(evts: AttemptEvent[] | undefined) {
    const events = (evts ?? []).slice().sort((a, b) => a.ts - b.ts);
    const GAP_MS = 2000; // events within 2s of previous and same normalized type will be grouped
    const groups: Array<{
      id: number;
      type: string; // normalized type
      startTs: number;
      endTs: number;
      count: number;
      events: AttemptEvent[];
    }> = [];

    let id = 0;
    for (const ev of events) {
      const norm = normalizeEventType(ev.type);
      if (groups.length === 0) {
        groups.push({ id: id++, type: norm, startTs: ev.ts, endTs: ev.ts, count: 1, events: [ev] });
        continue;
      }
      const last = groups[groups.length - 1]!;
      if (norm === last.type && ev.ts - last.endTs <= GAP_MS) {
        last.events.push(ev);
        last.endTs = ev.ts;
        last.count += 1;
      } else {
        groups.push({ id: id++, type: norm, startTs: ev.ts, endTs: ev.ts, count: 1, events: [ev] });
      }
    }
    return groups;
  }

  function colorForEventType(type: string) {
    switch (type) {
      case "typing":
        return "bg-sky-400";
      case "paste":
        return "bg-emerald-400";
      case "idle-start":
      case "idle-end":
      case "idle":
        return "bg-yellow-400";
      case "blur":
      case "visibilitychange":
      case "focus-lost":
      case "focus":
        return "bg-rose-400";
      case "submit":
        return "bg-green-600";
      default:
        return "bg-gray-400";
    }
  }

  function fmtTime(ts: number) {
    return new Date(ts).toLocaleTimeString();
  }

  function humanizeType(t: string) {
    if (!t) return "";
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  function prettyEvent(ev: AttemptEvent) {
    const raw = (ev.type || "").toLowerCase();
    const norm = normalizeEventType(ev.type);
    let label = "";
    let detail = "";

    if (norm === "typing") {
      label = "Typing";
      if (raw.startsWith("key") || raw === "keydown") {
        if (ev.data && typeof ev.data === "object") {
          const d: any = ev.data as any;
          const mods: string[] = [];
          if (d.ctrl) mods.push("Ctrl");
          if (d.shift) mods.push("Shift");
          if (d.alt) mods.push("Alt");
          const modStr = mods.length ? ` [${mods.join("+")}]` : "";
          detail = `${d.key ?? ""}${d.code ? ` (${d.code})` : ""}${modStr}`.trim();
        } else {
          detail = String(ev.data ?? "");
        }
      } else {
        // input event
        if (ev.data && typeof ev.data === "object") {
          const d: any = ev.data as any;
          const parts: string[] = [];
          if (typeof d.len === "number") parts.push(`length ${d.len}`);
          if (typeof d.caret === "number") parts.push(`caret ${d.caret}`);
          detail = parts.join(", ") || JSON.stringify(ev.data);
        } else {
          detail = String(ev.data ?? "");
        }
      }
    } else if (norm === "paste") {
      label = "Paste";
      if (ev.data && typeof ev.data === "object") {
        const d: any = ev.data as any;
        const len = d.len ?? d.pastedLen ?? d.pastedLength;
        detail = typeof len === "number" ? `pasted ${len} chars` : JSON.stringify(ev.data);
      } else if (typeof ev.data === "string") {
        detail = `pasted ${ev.data.length} chars`;
      }
    } else if (norm === "idle") {
      label = "Idle";
      if (ev.type === "idle-start" || (ev.data && (ev.data as any).since)) {
        const since = (ev.data as any).since ?? (ev.data as any).idleFor;
        if (typeof since === "number") detail = `${since} ms`;
      } else if (ev.type === "idle-end" || (ev.data && (ev.data as any).idleFor)) {
        const idleFor = (ev.data as any).idleFor;
        if (typeof idleFor === "number") detail = `${idleFor} ms`;
      }
    } else if (norm === "focus") {
      // blur / focus / visibility
      label = raw === "blur" || raw === "focus-lost" ? "Blur" : raw === "focus" ? "Focus" : "Visibility";
      if (ev.data && typeof ev.data === "object") {
        const d: any = ev.data as any;
        if (d.reason) detail = String(d.reason);
        const state = d.visibilityState ?? d.state;
        if (state) detail = `visibility: ${state}`;
      }
    } else if (norm === "submit") {
      label = "Submit";
      if (ev.data && typeof ev.data === "object") {
        const d: any = ev.data as any;
        if (d.submission !== undefined) detail = String(d.submission);
      }
    } else {
      label = humanizeType(norm || raw);
      detail = typeof ev.data === "string" ? ev.data : JSON.stringify(ev.data ?? {});
    }

    return { label, detail };
  }

  const selectedAttemptGroups = useMemo(() => groupAttemptEvents(selectedAttempt?.events), [selectedAttempt?.events]);

  useEffect(() => {
    // reset to first page whenever filters/pageSize change
    setPage(1);
  }, [filterUser, filterText, filterCorrect, pageSize, filterStart, filterEnd]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resp = await getAttempts({
        page,
        pageSize,
        user: filterUser || null,
        text: filterText || null,
        correct: filterCorrect === "all" ? null : filterCorrect,
        start: filterStart || null,
        end: filterEnd || null,
      });
      if (cancelled) return;
      setServerItems(resp.items ?? []);
      setServerTotal(resp.total ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, filterUser, filterText, filterCorrect, filterStart, filterEnd]);

  function toggleGroup(id: number) {
    setExpandedGroups((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <div className="w-full mt-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Admin Page</CardTitle>
          <CardDescription>Admin-only area</CardDescription>
        </CardHeader>

        <CardContent>
          <p>
            Welcome, <strong>{user?.username}</strong>
          </p>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Rounding Attempts</h3>
              <div className="flex gap-2">
                <Button onClick={async () => { setAttempts(await getAllAttempts()); const resp = await getAttempts({ page, pageSize, user: filterUser || null, text: filterText || null, correct: filterCorrect === "all" ? null : filterCorrect, start: filterStart || null, end: filterEnd || null }); setServerItems(resp.items ?? []); setServerTotal(resp.total ?? 0); }} variant="ghost">Refresh</Button>
                <Button
                  onClick={() => {
                    // export CSV
                    const rows = [
                      ["timestamp", "user", "taskText", "rule", "submission", "correct", "focusLost", "timeMs", "attemptNumber"],
                      ...attempts.map((a) => [
                        new Date(a.timestamp).toISOString(),
                        a.user,
                        a.taskText.replace(/\n/g, " "),
                        (a.rule as any).type === "nearest"
                          ? `nearest ${(a.rule as any).unit}`
                          : (a.rule as any).type === "decimals"
                            ? `decimals ${(a.rule as any).places}`
                            : (a.rule as any).type === "conversion"
                              ? `${(a.rule as any).from}->${(a.rule as any).to}`
                              : String((a.rule as any).type),
                        a.submission,
                        String(a.correct),
                        String(a.focusLost),
                        String(a.timeMs),
                        String(a.attemptNumber),
                      ]),
                    ];
                    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const el = document.createElement("a");
                    el.href = url;
                    el.download = "rounding_attempts.csv";
                    document.body.appendChild(el);
                    el.click();
                    el.remove();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export CSV
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} className="border rounded px-2 py-1 text-sm">
                <option value="">All users</option>
                {users.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>

              <select value={filterCorrect} onChange={(e) => setFilterCorrect(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
                <option value="all">All</option>
                <option value="correct">Correct</option>
                <option value="incorrect">Incorrect</option>
              </select>

              <input
                type="text"
                placeholder="Search task, submission or user"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="border rounded px-2 py-1 text-sm min-w-[220px]"
              />

              <label className="text-sm">From</label>
              <input
                type="date"
                value={filterStart ?? ""}
                onChange={(e) => setFilterStart(e.target.value || null)}
                className="border rounded px-2 py-1 text-sm"
              />
              <label className="text-sm">To</label>
              <input
                type="date"
                value={filterEnd ?? ""}
                onChange={(e) => setFilterEnd(e.target.value || null)}
                className="border rounded px-2 py-1 text-sm"
              />

              <select value={String(pageSize)} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1 text-sm">
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>

              <Button variant="ghost" onClick={() => { setFilterUser(""); setFilterText(""); setFilterCorrect("all"); setPage(1); }}>
                Reset
              </Button>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <tr>
                    <TableHead>When</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Rule</TableHead>
                    <TableHead>Submission</TableHead>
                    <TableHead>Correct</TableHead>
                    <TableHead>Focus Lost</TableHead>
                    <TableHead>Time (ms)</TableHead>
                    <TableHead>Attempt #</TableHead>
                    <TableHead>Timeline</TableHead>
                  </tr>
                </TableHeader>
                <TableBody>
                  {serverItems.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{new Date(a.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{a.user}</TableCell>
                      <TableCell className="max-w-md break-words">{a.taskText}</TableCell>
                      <TableCell>
                        {(a.rule as any).type === "nearest"
                          ? `nearest ${(a.rule as any).unit}`
                          : (a.rule as any).type === "decimals"
                            ? `decimals ${(a.rule as any).places}`
                            : (a.rule as any).type === "conversion"
                              ? `${(a.rule as any).from}->${(a.rule as any).to}`
                              : String((a.rule as any).type)}
                      </TableCell>
                      <TableCell>{a.submission}</TableCell>
                      <TableCell>{a.correct ? "yes" : "no"}</TableCell>
                      <TableCell>{a.focusLost ? "yes" : "no"}</TableCell>
                      <TableCell>{a.timeMs}</TableCell>
                      <TableCell>{a.attemptNumber}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedAttempt(a)}>Timeline</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-muted-foreground">Showing {serverItems.length} of {serverTotal} attempts</div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</Button>
                <div className="text-sm">Page {page} / {Math.max(1, Math.ceil(serverTotal / pageSize))}</div>
                <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.min(Math.max(1, Math.ceil(serverTotal / pageSize)), p + 1))} disabled={page >= Math.max(1, Math.ceil(serverTotal / pageSize))}>Next</Button>
              </div>
            </div>
          </div>

          {selectedAttempt && (
            <div className="mt-4 rounded-md border p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">Timeline for attempt {selectedAttempt.id}</h4>
                <div>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedAttempt(null)}>Close</Button>
                </div>
              </div>
              <div className="text-xs">
                {selectedAttemptGroups.length === 0 && (
                  <div className="text-xs text-muted-foreground">No timeline events recorded for this attempt.</div>
                )}

                {selectedAttemptGroups.map((group) => {
                  const attemptStart = (selectedAttempt.timestamp || 0) - (selectedAttempt.timeMs || 0);
                  const totalMs = Math.max(1, selectedAttempt.timeMs || (group.endTs - group.startTs));
                  const leftPercent = Math.max(0, ((group.startTs - attemptStart) / totalMs) * 100);
                  const widthPercent = Math.max(0.5, ((group.endTs - group.startTs) / totalMs) * 100);
                  const colorClass = colorForEventType(group.type);
                  return (
                    <div key={group.id} className="border-b py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`inline-block w-3 h-3 rounded ${colorClass}`} />
                          <div>
                            <div className="font-medium text-sm">
                              {group.type} <span className="text-xs text-muted-foreground">({group.count})</span>
                            </div>
                            <div className="text-muted-foreground text-[11px]">
                              {fmtTime(group.startTs)} — {fmtTime(group.endTs)} ({group.endTs - group.startTs} ms)
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-40 h-3 bg-gray-100 rounded relative overflow-hidden">
                            <div
                              className={`absolute top-0 h-3 rounded ${colorClass}`}
                              style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                            />
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => toggleGroup(group.id)}>
                            {expandedGroups.includes(group.id) ? "Hide" : "Show"}
                          </Button>
                        </div>
                      </div>

                      {expandedGroups.includes(group.id) && (
                        <div className="mt-2 text-xs">
                          {group.events.map((ev, idx) => {
                            const p = prettyEvent(ev);
                            return (
                              <div key={idx} className="border-t pt-1">
                                <div className="text-muted-foreground text-[11px]">{fmtTime(ev.ts)}</div>
                                <div>
                                  <strong>{p.label}</strong>
                                  {p.detail ? (
                                    <span className="ml-2 text-muted-foreground">— {p.detail}</span>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>User Activity</CardTitle>
          <CardDescription>See per-user attempts for today and view detailed stats.</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">Users</h3>
            <div className="flex gap-2">
              <Button onClick={async () => setAttempts(await getAllAttempts())} variant="ghost">Refresh</Button>
            </div>
          </div>

          <div className="rounded-md border mb-4">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>User</TableHead>
                  <TableHead>Attempts Today</TableHead>
                  <TableHead>Total Attempts</TableHead>
                  <TableHead>Actions</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u}>
                    <TableCell>{u}</TableCell>
                    <TableCell>{attemptsByUserToday.get(u) ?? 0}</TableCell>
                    <TableCell>{attempts.filter((a) => a.user === u).length}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(u); setSelectedDay(new Date().toISOString().slice(0, 10)); }}>
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {selectedUser && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Stats for {selectedUser}</h4>
              <div className="mb-4">
                <label className="mr-2">Select day:</label>
                <input
                  type="date"
                  value={selectedDay ?? new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setSelectedDay(e.target.value)}
                />
                <Button className="ml-4" variant="ghost" onClick={() => { setSelectedUser(null); setSelectedDay(null); }}>
                  Close
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-md border p-3">
                  <h5 className="text-sm font-medium">Per-day (last 30 days)</h5>
                  <div className="mt-2 text-xs">
                    <table className="w-full text-left">
                      <thead>
                        <tr><th>Date</th><th>Count</th></tr>
                      </thead>
                      <tbody>
                        {getPerDayCounts(selectedUser, 30).map((r) => (
                          <tr key={r.day}><td>{r.day}</td><td>{r.count}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-md border p-3">
                  <h5 className="text-sm font-medium">Per-hour ({selectedDay})</h5>
                  <div className="mt-2 text-xs">
                    <table className="w-full text-left">
                      <thead>
                        <tr><th>Hour</th><th>Count</th></tr>
                      </thead>
                      <tbody>
                        {(selectedDay ? getPerHourCounts(selectedUser, selectedDay) : []).map((h) => (
                          <tr key={h.hour}><td>{String(h.hour).padStart(2, '0')}:00</td><td>{h.count}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Admin;
